package wat.ui

import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import org.w3c.dom.*
import wat.i18n.I18n
import wat.i18n.I18n.t
import wat.model.*
import wat.service.DataService

object OnboardingScreen {
    fun render(userId: String, onDone: () -> Unit): HTMLElement {
        var height = ""
        var weight = ""
        var goalWeight = ""
        var sex = "m"
        var birthdate = ""
        var lang = I18n.currentLang

        val INP = "background:${Styles.TEXT_06};border:none;border-radius:10px;padding:12px 14px;color:${Styles.TEXT};font-size:16px;font-family:inherit;outline:none;width:100%;box-sizing:border-box;font-weight:700"
        val LBL = "font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1.5px;color:${Styles.TEXT_30};margin-bottom:6px;display:block"

        val container = div {
            style.cssText = "min-height:100vh;background:${Styles.BG};display:flex;align-items:flex-start;justify-content:center;padding:clamp(32px,6vh,60px) 16px 32px"
        }
        val wrapper = div { style.cssText = "max-width:380px;width:100%" }

        // Logo - small, subtle
        wrapper.appendChild(div {
            style.cssText = "text-align:center;margin-bottom:32px"
            appendChild(span("TRACTOR") {
                style.cssText = "font-size:28px;font-weight:900;letter-spacing:3px;color:${Styles.TEXT_15}"
            })
        })

        val card = div { style.cssText = "padding:0" }

        fun rebuildCard() {
            card.clear()

            // Language - pill selector
            card.appendChild(div {
                style.cssText = "margin-bottom:20px"
                appendChild(label(t("label_language")) { style.cssText = LBL })
                appendChild(div {
                    style.cssText = "display:flex;gap:4px;background:${Styles.INPUT_BG};border-radius:10px;padding:3px"
                    I18n.langNames.forEach { (code, name) ->
                        val isActive = lang == code
                        appendChild(button(name) {
                            val langBtnBg = if (isActive) { if (ThemeManager.isDark()) Styles.TEXT_10 else "#eeeef4" } else "transparent"
                            style.cssText = "flex:1;padding:8px 4px;border-radius:8px;border:none;font-size:12px;font-weight:${if (isActive) "700" else "500"};cursor:pointer;background:$langBtnBg;color:${if (isActive) "${Styles.TEXT}" else "${Styles.TEXT_30}"};font-family:inherit;transition:all 0.15s"
                            onclick = { lang = code; I18n.currentLang = code; rebuildCard() }
                        })
                    }
                })
            })

            // Height + Gender row
            card.appendChild(div {
                style.cssText = "display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:16px"

                // Height
                appendChild(div {
                    appendChild(label(t("onboard_height")) { style.cssText = LBL })
                    appendChild(div {
                        style.cssText = "display:flex;align-items:center;gap:6px"
                        appendChild(input("number") {
                            style.cssText = "$INP;flex:1"
                            value = height
                            placeholder = "175"
                            oninput = { e -> height = (e.target as HTMLInputElement).value; Unit }
                        })
                        appendChild(span("cm") { style.cssText = "font-size:13px;font-weight:600;color:${Styles.TEXT_TER}" })
                    })
                })

                // Gender
                appendChild(div {
                    appendChild(label(t("label_gender")) { style.cssText = LBL })
                    appendChild(div {
                        style.cssText = "display:flex;gap:6px"
                        listOf("m" to t("gender_male"), "f" to t("gender_female")).forEach { (k, lbl) ->
                            val isActive = sex == k
                            val c = if (k == "f") "#f472b6" else Styles.A1
                            appendChild(button(lbl) {
                                val gInactiveBg = if (ThemeManager.isDark()) Styles.TEXT_04 else Styles.INPUT_BG
                                style.cssText = "flex:1;padding:12px 4px;border-radius:10px;border:none;background:${if (isActive) "${c}18" else gInactiveBg};color:${if (isActive) c else "${Styles.TEXT_TER}"};font-size:14px;font-weight:700;cursor:pointer;font-family:inherit;transition:all 0.15s"
                                onclick = { sex = k; rebuildCard() }
                            })
                        }
                    })
                })
            })

            // Birthdate
            val age = if (birthdate.isNotBlank()) calcAge(birthdate) else -1
            val ageStr = if (age > 0) " ($age ${t("years_short")})" else ""
            val ageInvalid = birthdate.isNotBlank() && (age < 6 || age > 110)

            card.appendChild(div {
                style.cssText = "margin-bottom:16px"
                appendChild(div {
                    style.cssText = "display:flex;align-items:center;gap:6px;margin-bottom:6px"
                    appendChild(label(t("label_birthdate")) { style.cssText = LBL + ";margin-bottom:0" })
                    if (ageStr.isNotBlank()) {
                        appendChild(span(ageStr) { style.cssText = "font-size:11px;font-weight:700;color:${if (ageInvalid) "#fb7185" else "#818cf8"}" })
                    }
                })
                appendChild(input("date") {
                    style.cssText = "$INP;color-scheme:${Styles.COLOR_SCHEME}"
                    value = birthdate
                    oninput = { e -> birthdate = (e.target as HTMLInputElement).value; rebuildCard() }
                })
                if (ageInvalid) {
                    appendChild(div {
                        style.cssText = "margin-top:6px;font-size:12px;font-weight:600;color:#fb7185"
                        textContent = t("onboard_err_birthdate")
                    })
                }
            })

            // Weight + Goal row
            val wtVal = weight.toDoubleOrNull() ?: 0.0
            val wtInvalid = weight.isNotBlank() && wtVal > 0 && wtVal !in 20.0..300.0
            val gwVal = goalWeight.toDoubleOrNull() ?: 0.0
            val gwInvalid = goalWeight.isNotBlank() && gwVal > 0 && gwVal !in 20.0..300.0

            card.appendChild(div {
                style.cssText = "display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:16px"

                appendChild(div {
                    appendChild(label(t("onboard_weight")) { style.cssText = LBL })
                    appendChild(div {
                        style.cssText = "display:flex;align-items:center;gap:6px"
                        appendChild(input("number") {
                            step = "0.1"
                            style.cssText = "$INP;flex:1"
                            value = weight
                            placeholder = "80"
                            oninput = { e -> weight = (e.target as HTMLInputElement).value; rebuildCard() }
                        })
                        appendChild(span("kg") { style.cssText = "font-size:13px;font-weight:600;color:${Styles.TEXT_TER}" })
                    })
                    if (wtInvalid) {
                        appendChild(div {
                            style.cssText = "margin-top:4px;font-size:11px;font-weight:600;color:#fb7185"
                            textContent = t("onboard_err_weight")
                        })
                    }
                })

                appendChild(div {
                    appendChild(label(t("onboard_goal")) { style.cssText = LBL })
                    appendChild(div {
                        style.cssText = "display:flex;align-items:center;gap:6px"
                        appendChild(input("number") {
                            step = "0.1"
                            style.cssText = "$INP;flex:1"
                            value = goalWeight
                            placeholder = "75"
                            oninput = { e -> goalWeight = (e.target as HTMLInputElement).value; rebuildCard() }
                        })
                        appendChild(span("kg") { style.cssText = "font-size:13px;font-weight:600;color:${Styles.TEXT_TER}" })
                    })
                    if (gwInvalid) {
                        appendChild(div {
                            style.cssText = "margin-top:4px;font-size:11px;font-weight:600;color:#fb7185"
                            textContent = t("onboard_err_weight")
                        })
                    }
                })
            })

            // Submit button
            val htV = height.toIntOrNull() ?: 0
            val wtV = weight.toDoubleOrNull() ?: 0.0
            val bdValid = birthdate.isNotBlank() && calcAge(birthdate) in 6..110
            val valid = htV in 80..250 && wtV in 20.0..300.0 && bdValid

            card.appendChild(button(t("onboard_start")) {
                style.cssText = if (valid) {
                    "width:100%;padding:14px;border-radius:12px;border:none;background:${Styles.A1};color:#000;font-size:15px;font-weight:800;cursor:pointer;letter-spacing:0.5px"
                } else {
                    "width:100%;padding:14px;border-radius:12px;border:none;background:${Styles.INPUT_BG};color:${Styles.TEXT_20};font-size:15px;font-weight:800;cursor:default;letter-spacing:0.5px"
                }
                disabled = !valid
                onclick = {
                    if (valid) {
                        GlobalScope.launch {
                            val prof = Profile(
                                height = htV,
                                sex = sex,
                                goalWeight = goalWeight.toDoubleOrNull() ?: 0.0,
                                birthdate = birthdate,
                                lang = lang
                            )
                            DataService.saveProfile(userId, prof)
                            DataService.saveEntry(userId, todayLocal(), wtV, emptyList(), "")
                            onDone()
                        }
                    }
                }
            })
        }

        rebuildCard()
        wrapper.appendChild(card)
        container.appendChild(wrapper)
        return container
    }
}
